/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import { Newsletters } from "App";
import {
    NewsletterCount, NewsletterPostStats, NewsletterSend, NewsletterSendRecipients,
    NewsletterSummary
} from "App/Models/Newsletter";

import { FunnelStage } from "./Stage";

/* LES STATISTIQUES EN DEMONSTRATION ( Fred, 22/10/2026 : « mets-moi de fausses donnees
   dans tous les blocs que le back peut remplir, pour que je puisse travailler le
   design — ne m'invente pas de donnees, regarde ce que le back sait mettre » ).

   Ce qui est VRAI : la forme des reponses ( les contrats de `UseCases/Newsletters` ),
   les articles du client ( `group/{id}/blogs/{blog}/posts` : identifiant, titre,
   adresse ) et ses contacts ( `group/{id}/contacts` ), lus par le relais de la
   Console. Meme module que le Desktop ( `Newsletter/Automatic/Demo.ts` ).
   Ce qui est FABRIQUE : les chiffres ( envois,
   ouvertures, clics, desinscriptions ), stables d'un chargement a l'autre.

   ⚠️ Ne s'allume qu'avec `?demo=1` dans l'adresse ET hors build de production :
   rien de ceci ne peut s'afficher chez un client. Le back ne renvoie pas d'image
   dans les statistiques : il n'y en a pas ici non plus. */

export function IsDemo( query: { [ key: string ]: unknown } ): boolean {
    return process.env.NODE_ENV !== "production" && String( query.demo || "" ) === "1";
}

interface DemoPost { id: number; title: string; url: string }
interface DemoContact {
    id: number;
    firstname: string | null;
    lastname: string | null;
    email: string;
}

export interface DemoData {
    summary( from?: string, to?: string ): NewsletterSummary;
    sends: NewsletterSend[];
    /* Une page des contacts d'un envoi, comme la route ( `page`, `limit` ). */
    recipients(
        sendId: number, page?: number, limit?: number
    ): NewsletterSendRecipients;
    posts( from?: string, to?: string ): NewsletterPostStats[];
    /* L'etage d'un article : FABRIQUE, comme les chiffres, pour voir le graphique a
       bulles rempli. */
    stage( postId: number ): FunnelStage;
}

/* Un tirage pseudo-aleatoire a graine fixe : les memes chiffres a chaque ouverture. */
function seeded( seed: number ): () => number {
    let state = seed;

    return (): number => {
        state = ( state * 1664525 + 1013904223 ) % 4294967296;

        return state / 4294967296;
    };
}

/* Les titres des flux RSS gardent leurs entites ( « &rsquo; » ) : la Console n'a pas
   de decodeur, on passe par le navigateur. */
function text( value: unknown ): string {
    if( typeof value !== "string" )
        return "";

    const area = document.createElement( "textarea" );

    area.innerHTML = value;

    return area.value;
}

async function realPosts( groupId: number ): Promise<DemoPost[]> {
    try {
        const [ blogs ] = await Newsletters.Blogs( groupId );
        const all: any[] = [];

        for( const blog of blogs || [] ) {
            const [ posts ] = await Newsletters.BlogPosts( groupId, blog.id, 20 );

            all.push( ...( posts || [] ) );
        }

        const seen: { [ title: string ]: boolean } = {};

        /* Un meme article importe deux fois ( deux flux ) n'est garde qu'une fois. */
        return all
            .filter( ( post: any ): boolean => post !== null && post.status !== "inactive" )
            .map( ( post: any ): DemoPost => ( {
                id: Number( post.id ), title: text( post.title ), url: text( post.url )
            } ) )
            .filter( ( post: DemoPost ): boolean => {
                if( post.title === "" || seen[ post.title ] )
                    return false;

                seen[ post.title ] = true;

                return true;
            } )
            .slice( 0, 16 );
    } catch( _ ) {
        return [];
    }
}

async function realContacts( groupId: number ): Promise<DemoContact[]> {
    try {
        const [ contacts ] = await Newsletters.Contacts( groupId, "", 60 );

        return ( contacts || [] as any[] )
            .filter( ( contact: any ): boolean => !!contact && !!contact.email )
            .map( ( contact: any ): DemoContact => ( {
                id: Number( contact.id ),
                firstname: contact.firstname || null,
                lastname: contact.lastname || null,
                email: String( contact.email )
            } ) )
            .slice( 0, 40 );
    } catch( _ ) {
        return [];
    }
}

function iso( date: Date ): string {
    return date.toISOString();
}

function dayOf( value: string ): string {
    return value.slice( 0, 10 );
}

function round( value: number ): number {
    return Math.round( value * 10 ) / 10;
}

/* SIX MOIS, DOUZE ARTICLES PAR MOIS, UNE NEWSLETTER TOUTES LES DEUX SEMAINES ( Fred,
   22/10/2026 ) : 13 envois dont un saute, six articles par envoi, 72 articles. Les vrais
   articles du compte d'abord ; le compte n'en a qu'une quinzaine, les autres sont
   des « Article d'exemple n° N », sans adresse. */
const DEMO_ARTICLES = 72;
const DEMO_SENDS = 13;
export const DEMO_AUDIENCE = 3000;

/* Les contacts eligibles de la demonstration : 3 000 sur 3 400 vises. */
export const DEMO_COUNT: NewsletterCount = {
    matched: 3400,
    total: DEMO_AUDIENCE,
    excluded: { no_email: 312, not_authorized: 71, bounced: 17 }
};
const DEMO_PER_SEND = 6;
const DEMO_EVERY = 14 * 86400000;

function withExamples( posts: DemoPost[] ): DemoPost[] {
    const list = posts.slice( 0, DEMO_ARTICLES );

    for( let index = list.length; index < DEMO_ARTICLES; index++ )
        list.push( { id: 800000 + index, title: `Article d'exemple n° ${ index + 1 }`, url: "" } );

    return list;
}

/* Les donnees de demonstration de la newsletter : 13 envois du lundi a 9 h, un
   toutes les deux semaines ( six mois ), le plus recent la semaine derniere, dont un
   saute faute de contenu. Six articles par envoi, tous differents. */
export async function LoadDemo(
    groupId: number,
    recipientsCount: number,
    nextSendAt: string | null
): Promise<DemoData> {
    const [ found, contacts ] = await Promise.all( [
        realPosts( groupId ), realContacts( groupId )
    ] );
    const posts = withExamples( found );
    const random = seeded( 2026 );
    /* 3 000 contacts ( Fred, 22/10/2026 ), quel que soit le compte. */
    const audience = DEMO_AUDIENCE;

    const monday = new Date();

    monday.setHours( 9, 0, 0, 0 );
    monday.setDate( monday.getDate() - ( ( monday.getDay() + 6 ) % 7 ) - 7 );

    const sends: NewsletterSend[] = [];
    const sentPosts: { [ sendId: number ]: number[] } = {};
    const slots = [ 0, 1, 2, 3, 4, 5 ].slice( 0, DEMO_PER_SEND );
    let slot = 0;

    for( let index = 0; index < DEMO_SENDS; index++ ) {
        const date = new Date( monday.getTime() - index * DEMO_EVERY );
        const id = 9000 + DEMO_SENDS - index;
        const skipped = index === 5;
        const postIds = skipped || posts.length === 0
            ? []
            : slots.map(
                ( shift ): number => posts[ ( slot * DEMO_PER_SEND + shift ) % posts.length ].id
            );

        sentPosts[ id ] = postIds;

        if( !skipped )
            slot++;

        if( skipped ) {
            sends.push( {
                id, status: "skipped_no_article", date: iso( date ), scheduled_for: iso( date ),
                since: iso( new Date( date.getTime() - DEMO_EVERY ) ),
                subject: null, posts_count: 0, targeted: DEMO_COUNT.matched,
                recipients_count: 0, campaign_id: null, error: null, metrics: null
            } );
            continue;
        }

        const sent = audience - Math.floor( random() * 6 );
        const bounced = Math.round( sent * ( 0.004 + random() * 0.008 ) );
        const delivered = sent - bounced;
        /* 20 % d'ouverture en moyenne ( Fred, 22/10/2026 ). */
        const opened = Math.round( delivered * ( 0.17 + random() * 0.06 ) );
        const clicked = Math.round( opened * ( 0.12 + random() * 0.14 ) );
        const unsubscribed = Math.floor( random() * 9 );

        sends.push( {
            id, status: "scheduled", date: iso( date ), scheduled_for: iso( date ),
            since: iso( new Date( date.getTime() - DEMO_EVERY ) ),
            subject: "Vos actualités immobilières — recette",
            posts_count: postIds.length, targeted: DEMO_COUNT.matched, recipients_count: sent,
            campaign_id: 50000 + id, error: null,
            metrics: {
                sent, delivered, opened, clicked, bounced, unsubscribed,
                spam_reports: index === 2 ? 1 : 0,
                open_rate: round( opened / delivered * 100 ),
                click_rate: round( clicked / delivered * 100 )
            }
        } );
    }

    const inPeriod = ( send: NewsletterSend, from?: string, to?: string ): boolean =>
        ( from === undefined || dayOf( send.date ) >= from ) &&
        ( to === undefined || dayOf( send.date ) <= to );

    const summary = ( from?: string, to?: string ): NewsletterSummary => {
        const chosen = sends.filter( ( send ): boolean => inPeriod( send, from, to ) );
        const done = chosen.filter( ( send ): boolean => send.metrics !== null );
        const sum = ( pick: ( send: NewsletterSend ) => number ): number =>
            done.reduce( ( total, send ): number => total + pick( send ), 0 );
        const delivered = sum( ( send ): number => send.metrics!.delivered );
        const opened = sum( ( send ): number => send.metrics!.opened );
        const clicked = sum( ( send ): number => send.metrics!.clicked );
        const last = done[ 0 ];

        return {
            period: {
                from: from ||
                    ( chosen.length > 0 ? dayOf( chosen[ chosen.length - 1 ].date ) : "" ),
                to: to || dayOf( iso( new Date() ) )
            },
            sends: {
                done: done.length,
                skipped_no_article: chosen.filter(
                    ( s ): boolean => s.status === "skipped_no_article"
                ).length,
                skipped_other: 0,
                failed: 0,
                next_send_at: nextSendAt
            },
            last_send: last === undefined ? null : {
                id: last.id,
                date: last.date,
                targeted: last.targeted,
                excluded: DEMO_COUNT.excluded,
                recipients: last.recipients_count
            },
            emails: {
                sent: sum( ( send ): number => send.metrics!.sent ),
                delivered,
                bounced: sum( ( send ): number => send.metrics!.bounced )
            },
            opens: {
                unique: opened,
                rate: delivered > 0 ? round( opened / delivered * 100 ) : null
            },
            clicks: {
                unique: clicked,
                rate: delivered > 0 ? round( clicked / delivered * 100 ) : null,
                click_to_open: opened > 0 ? round( clicked / opened * 100 ) : null
            },
            unsubscribed: sum( ( send ): number => send.metrics!.unsubscribed ),
            spam_reports: sum( ( send ): number => send.metrics!.spam_reports ),
            blog_visits: Math.round( clicked * 0.9 ),
            leads: null,
            curve: done.slice().reverse().map( ( send ): NewsletterSummary[ "curve" ][ 0 ] => ( {
                send_id: send.id,
                date: send.date,
                open_rate: send.metrics!.open_rate,
                click_rate: send.metrics!.click_rate,
                unsubscribed: send.metrics!.unsubscribed
            } ) )
        };
    };

    const title = ( id: number ): DemoPost =>
        posts.find( ( post ): boolean => post.id === id ) || { id, title: `#${ id }`, url: "" };

    const recipients = (
        sendId: number, page: number = 1, limit: number = 50
    ): NewsletterSendRecipients => {
        const send = sends.find( ( item ): boolean => item.id === sendId );
        const ids = sentPosts[ sendId ] || [];
        const local = seeded( sendId );
        const rows = contacts.map( ( contact ): NewsletterSendRecipients[ "contacts" ][ 0 ] => {
            const opened = local() < 0.4;
            const clicked = opened && local() < 0.3;

            return {
                contact_id: contact.id,
                firstname: contact.firstname,
                lastname: contact.lastname,
                email: contact.email,
                posts_received: ids,
                sent: true,
                delivered: true,
                opened,
                clicked,
                bounced: false,
                posts_clicked: clicked ? [ ids[ Math.floor( local() * ids.length ) ] ] : []
            };
        } );

        const start = ( Math.max( 1, page ) - 1 ) * limit;

        return {
            contacts: rows.slice( start, start + limit ),
            posts: ids.map( ( id ): NewsletterSendRecipients[ "posts" ][ 0 ] => ( {
                id,
                title: title( id ).title,
                url: title( id ).url,
                recipients: send !== undefined ? send.recipients_count : 0,
                clicks: rows.filter(
                    ( row ): boolean => row.posts_clicked.indexOf( id ) >= 0
                ).length
            } ) ),
            total: rows.length
        };
    };

    const postsStats = ( from?: string, to?: string ): NewsletterPostStats[] => {
        const totals: { [ id: number ]: NewsletterPostStats } = {};

        const done = sends.filter(
            ( send ): boolean => send.metrics !== null && inPeriod( send, from, to )
        );

        done.forEach( ( send ): void => {
            const ids = sentPosts[ send.id ] || [];

            ids.forEach( ( id, rank ): void => {
                const post = title( id );
                const row = totals[ id ] || {
                    id, title: post.title, url: post.url,
                    times_sent: 0, contacts_reached: 0, clicks: 0, blog_visits: 0,
                    opened: 0, average_position: 0
                };
                /* Chaque contact recoit SES articles : un article n'est vu que par une
                   part des ouvreurs de l'envoi, et son attrait varie d'un article a
                   l'autre ( fabrique, stable par article ). */
                const share = 0.3 + ( id * 37 % 70 ) / 100;
                const appeal = 0.6 + ( id * 53 % 90 ) / 100;
                const opened = Math.max( 1, Math.round( send.metrics!.opened * share ) );
                const clicks = Math.min( opened, Math.round(
                    opened * [ 0.09, 0.066, 0.051, 0.039, 0.03, 0.024 ][ rank ] * appeal
                ) );

                row.average_position =
                    ( ( row.average_position || 0 ) * row.times_sent + rank + 1 ) /
                    ( row.times_sent + 1 );
                row.times_sent += 1;
                row.contacts_reached += Math.round( send.metrics!.delivered * share );
                row.opened = ( row.opened || 0 ) + opened;
                row.clicks += clicks;
                row.blog_visits = ( row.blog_visits || 0 ) + Math.round( clicks * 0.9 );
                totals[ id ] = row;
            } );
        } );

        return Object.keys( totals )
            .map( ( key ): NewsletterPostStats => totals[ Number( key ) ] )
            .sort( ( a, b ): number => b.clicks - a.clicks );
    };

    /* 50 % TOFU, 30 % MOFU, 20 % BOFU ( Fred, 22/10/2026 ), meles dans l'ordre des
       articles. */
    const stages: FunnelStage[] = [
        "top", "middle", "top", "bottom", "top", "middle", "top", "middle", "bottom", "top"
    ];
    const stage = ( postId: number ): FunnelStage => {
        const index = posts.findIndex( ( post ): boolean => post.id === postId );

        return stages[ ( index < 0 ? postId : index ) % stages.length ];
    };

    return { summary, sends, recipients, posts: postsStats, stage };
}
